import { createAsyncThunk } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import {
  connectToIsDataReadyForLocation,
  informLocationChange,
} from 'services/location/location.service';
import { setSelectedLocationStatus } from 'store/slices/User/userSlice';
import { normalizePlaceForSelectedLocation } from 'store/utils/userLocation.utils';
import {
  notifyLoadingToast,
  resolveLoadingToast,
  updateLoadingToastProgress,
} from 'utils/notifications/toast.utils';

export const selectUserPlace = createAsyncThunk(
  'user/selectUserPlace',
  async (place, { rejectWithValue, dispatch }) => {
    if (!place) {
      return rejectWithValue('No place provided');
    }

    const name = place.title || place.term || place.city || 'location';
    // Persistent toast for the subscribe SSE: open as loading, live-update
    // with progress, then resolve through the shared toast pipeline so the
    // final pass/fail message behaves exactly like every other app toast.
    const toastId = notifyLoadingToast(`Preparing weather data for ${name}…`);

    try {
      await informLocationChange(place);

      // Subscribe is an SSE: it streams progress, then sends a single
      // pass ('ready') or fail ('error') message and closes the connection.
      await new Promise((resolve, reject) => {
        const eventSource = connectToIsDataReadyForLocation(place);

        eventSource
          .then((source) => {
            source.onerror = () => {
              source.close();
              reject(new Error('Lost connection while preparing weather data for this location.'));
            };

            source.onmessage = (event) => {
              const { message, status } = JSON.parse(event.data);

              if (status === 'ready') {
                source.close();
                resolve(message);
              } else if (status === 'error') {
                // Surface the real server message (e.g. "City ID not found…").
                source.close();
                reject(new Error(message || 'Could not prepare data for this location.'));
              } else {
                // 'fetching' / progress — stream it to the banner and the toast
                // so the user sees "Waiting for data in <location>…".
                dispatch(setSelectedLocationStatus({ status: 'checking', statusMessage: message }));
                updateLoadingToastProgress(toastId, message);
              }
            };
          })
          .catch(reject);
      });

      // Pass — connection closed with 'ready'.
      resolveLoadingToast(toastId, {
        kind: 'success',
        fallbackMessage: `Weather data ready for ${name}`,
      });
      Cookies.set('selectedPlaceId', place.place_id);
      return normalizePlaceForSelectedLocation(place);
    } catch (error) {
      // Fail — connection closed with 'error', or it never connected.
      const message = error?.message || 'Unable to prepare location';
      resolveLoadingToast(toastId, {
        kind: 'error',
        messagePayload: error,
        fallbackMessage: 'Unable to prepare location',
      });
      return rejectWithValue(message);
    }
  },
);
